import { Box, Typography } from "@mui/material";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import DateDisplay from "components/common/DateDisplay";
import DocumentViewer from "components/editor/DocumentViewer";
import useIsMobile from "components/hooks/useIsMobile";
import { AdvisorAnnotation, FamilyAnnotation } from "protogen/annotations_pb";
import FlagsDisplay from "./FlagsDisplay";
import DropdownMenu from "components/common/DropdownMenu";
import { EllipsisVertical } from "lucide-react";

const ActionMenu = ({ onDelete }: { onDelete: () => Promise<void> }) => {
  return (
    <DropdownMenu
      disabled={false}
      fullWidth
      buttonContent={<EllipsisVertical size={20} color="#8E9598" />}
      dropdownIcon={false}
      variant="text"
      options={[
        {
          title: "Delete",
          onClick: onDelete,
        },
      ]}
      sx={{
        minWidth: "auto",
        height: "unset",
      }}
    />
  );
};

type AnnotationCardProps = {
  annotation: FamilyAnnotation | AdvisorAnnotation;
  handleDelete?: (
    annotation: FamilyAnnotation | AdvisorAnnotation,
  ) => Promise<void>;
};

export default ({ annotation, handleDelete }: AnnotationCardProps) => {
  const isMobile = useIsMobile();
  return (
    <Box
      sx={{
        display: "flex",
        gap: "12px",
        flexGrow: 1,
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            height: "inherit",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <UserAvatar user={annotation.author!} size={40} />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FAF9FA",
          flexGrow: 1,
          borderRadius: "16px",
          padding: "16px 24px",
          gap: "8px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexGrow: 1,
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography sx={{ fontWeight: "700" }}>
              {annotation.author?.displayName}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#6B6E7B" }}>
              #{annotation.noteType}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <DateDisplay
              date={new Date(Number(annotation.lastUpdatedSec) * 1000)}
              sx={{ fontSize: "14px", color: "#6B6E7B" }}
            />
            <ActionMenu onDelete={async () => handleDelete?.(annotation)} />
          </Box>
        </Box>
        <Box>
          <DocumentViewer
            key={annotation.ref}
            content={JSON.parse(annotation.content)}
            attachments={annotation.attachments}
            minHeight="20px"
          />
        </Box>
        <FlagsDisplay
          flags={annotation.flags}
          flagUntilDate={
            annotation instanceof FamilyAnnotation
              ? annotation.flagUntilDate
              : undefined
          }
        />
      </Box>
    </Box>
  );
};
